import { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';

import { SectionTypes } from '@common/enums/section-type-enum';
import { ApplianceModel, ApplianceSource } from '@common/models';
import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useStores } from '@ee-api/stores-api/useStores';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { skipToken } from '@reduxjs/toolkit/query';

import {
    useFetchCustomerSuppliedCookingAppliancesQuery,
    useFetchInStoreCookingAppliancesQuery,
} from '../../../../../../api/static-content-api/cooking-appliance-api/cookingApplianceService';
import { makeCookingAppliancesSelector } from '../CookingAppliancesSection.utils';

/**
 * Defining empty array outside of selector as indicated in docs.
 * @see {@link https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results}
 */
const emptyArray = [] satisfies ApplianceModel[];

interface useCookingApplianceReturn {
    browsedCookingAppliances: ApplianceModel[];
    customerSuppliedCookingAppliances: ApplianceModel[];
    isFetchingCookingAppliances: boolean;
    isErrorCookingAppliances: boolean;
}

const useCookingAppliance = (): useCookingApplianceReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { stores } = useStores();
    const { isMaterialLoaded } = useMaterials();
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { isFetchingDependencies } = useFetchDependencies();
    const inStoreCookingAppliancesUrl = useRetailerSettings().getInStoreApplianceUrl(SectionTypes.cooking_appliance);

    const selectInStoreCookingAppliancesTransformed = useMemo(() => makeCookingAppliancesSelector(), []);
    const {
        inStoreCookingAppliances,
        isFetching: isFetchingInStoreCookingAppliances,
        isError: isErrorInStoreCookingAppliances,
    } = useFetchInStoreCookingAppliancesQuery(
        isFetchingDependencies ||
            !inStoreCookingAppliancesUrl ||
            !zipcode ||
            !selectedColor ||
            !stores.length ||
            !isMaterialLoaded
            ? skipToken
            : { url: inStoreCookingAppliancesUrl },
        {
            selectFromResult: ({ data, ...rest }) => ({
                inStoreCookingAppliances: selectInStoreCookingAppliancesTransformed(
                    data ?? emptyArray,
                    ApplianceSource.IN_STORE,
                    undefined,
                    true
                ),
                ...rest,
            }),
        }
    );

    const {
        customerSuppliedCookingAppliances,
        isFetching: isFetchingCustomerSuppliedCookingAppliances,
        isError: isErrorCustomerSuppliedCookingAppliances,
    } = useFetchCustomerSuppliedCookingAppliancesQuery(
        isFetchingDependencies || !zipcode || !selectedColor || !stores.length || !selectedColor.qteGrpID
            ? skipToken
            : {
                  params: {
                      zipCode: zipcode,
                      storeId: selectedStore?.companyId ?? undefined,
                      prodQteGrpID: selectedColor.qteGrpID,
                  },
              },
        {
            selectFromResult: ({ data, ...rest }) => {
                return {
                    customerSuppliedCookingAppliances: rest.isError ? emptyArray : data ?? emptyArray,
                    ...rest,
                };
            },
        }
    );

    return {
        browsedCookingAppliances: inStoreCookingAppliances,
        customerSuppliedCookingAppliances,
        isFetchingCookingAppliances: isFetchingInStoreCookingAppliances || isFetchingCustomerSuppliedCookingAppliances,
        isErrorCookingAppliances: isErrorInStoreCookingAppliances || isErrorCustomerSuppliedCookingAppliances,
    };
};

export default useCookingAppliance;
